/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentLess,
  insertTab,
} from '@codemirror/commands';
import {
  HighlightStyle,
  LanguageDescription,
  syntaxHighlighting,
} from '@codemirror/language';
import {
  Compartment,
  EditorState,
  Prec,
  type Extension,
  type Text,
  type TransactionSpec,
} from '@codemirror/state';
import {
  crosshairCursor,
  drawSelection,
  EditorView,
  highlightActiveLine,
  highlightActiveLineGutter,
  highlightSpecialChars,
  keymap,
  lineNumbers,
  placeholder,
  rectangularSelection,
  tooltips,
  type DOMEventHandlers,
  type KeyBinding,
  ViewPlugin,
  type PluginValue,
} from '@codemirror/view';
import {
  search,
  searchKeymap,
  highlightSelectionMatches,
  openSearchPanel,
  findNext,
  findPrevious,
} from '@codemirror/search';
import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
} from '@codemirror/autocomplete';
import {GuidGenerator} from '@packages/utilities/guid';
import {debounce} from 'lodash';
import {indentationMarkers} from '../extensions/indent-markers';
import {
  createSearchPanel,
  replaceVisibleExtension,
} from '../extensions/search-panel';
import {singleLineMode} from '../extensions/single-line-mode';
import {createTheme, type ThemeConfig} from '../theme/base';
import {wfCodeDarkHighlightStyle} from '../theme/wf-code-dark';
import {wfCodeLightHighlightStyle} from '../theme/wf-code-light';
import {languages, type LanguageName} from './languages';
import {markdownGfm} from '../languages/markdown';
import {flushOnChange, skipOnChange} from './annotations';
import {createEmmetExtensions} from '../extensions/emmet';

const emptyHighlightStyle = HighlightStyle.define([]);

function getHighlightStyleForTheme(
  theme?: string,
  highlightStyle?: 'default' | 'wf-code' | 'none'
) {
  if (highlightStyle === 'none') return emptyHighlightStyle;
  return theme?.startsWith('dark')
    ? wfCodeDarkHighlightStyle
    : wfCodeLightHighlightStyle;
}

export interface EditorConfig {
  element?: HTMLElement;
  initialDoc?: string | Text;
  placeholder?: string;
  height?: number | string;
  fontFamily?: string;
  language?: LanguageName | string;
  lineNumbers?: boolean;
  lineWrapping?: boolean;
  singleLine?: boolean;
  readOnly?: boolean;
  indentMarkers?: boolean;
  theme?: 'light' | 'dark' | 'dark-wf';
  highlightStyle?: 'default' | 'wf-code' | 'none';
  borderRadius?: number | string;
  markdown?: boolean;
  /** When true, excludes `{` from auto-closing brackets to avoid mangling `{{ }}` token syntax. */
  hasTokens?: boolean;
  /** When true, enables Emmet abbreviation expansion for supported languages (HTML, CSS, JSX, TSX). */
  emmet?: boolean;
  /**
   * When false, suppresses `highlightSelectionMatches`. Editors that paint widgets
   * over selected ranges (e.g. inline-binding tokens) collide with this plugin and
   * flicker on every selection change.
   */
  highlightSelectionMatches?: boolean;
  extensions?: Extension[];
  onChange?: (value: string, timestamp: DOMHighResTimeStamp) => void;
  onEnter?: (view: EditorView) => boolean | void;
  onFocus?: DOMEventHandlers<any>['focus'];
  onBlur?: DOMEventHandlers<any>['blur'];
  onKeyDown?: DOMEventHandlers<any>['keydown'];
  onScroll?: DOMEventHandlers<any>['scroll'];
  /** Called when Cmd/Ctrl+Shift+P is pressed. When provided, triggers formatting. */
  onFormat?: () => void;
  /** Called when Cmd/Ctrl+S is pressed. When provided, triggers save. */
  onSave?: () => void;
}

/**
 * The CodeMirror class is a wrapper around the CodeMirror 6 API. The EditorView is configurable with extensions to
 * support various use cases across the Webflow codebase. Modifiable extensions should be placed in `compartments` to
 * allow for changing their behavior at runtime.
 *
 * This class should only expose the bare minimum functionality to React components to support our use cases. The
 * EditorView updates within its own internal schedule, and is optimized for handling a large frequency of small changes
 * in accordance to how users interact with a code text editor. This means that the EditorView should avoid triggering
 * React re-renders as much as possible in order to prevent performance issues with its own update cycle.
 */
export class CodeMirror {
  /** The debounce duration for the change event in milliseconds, used to prevent excessive React re-renders. */
  static readonly changeDebounceMs = 200;
  /** Generates unique identifiers for each editor instance. */
  private static guid = new GuidGenerator();

  public config: EditorConfig;
  public docUpdatedAt: DOMHighResTimeStamp = 0;
  public isTyping: boolean = false;
  private typingTimeoutId: number | null = null;
  private editor: EditorView;
  private id: string;
  private compartments = {
    readOnly: new Compartment(),
    placeholder: new Compartment(),
    lineWrapping: new Compartment(),
    lineNumbers: new Compartment(),
    singleLine: new Compartment(),
    theme: new Compartment(),
    highlightStyle: new Compartment(),
    language: new Compartment(),
    indentMarkers: new Compartment(),
    markdown: new Compartment(),
    history: new Compartment(),
    domEventHandlers: new Compartment(),
    emmet: new Compartment(),
  };

  constructor(config: EditorConfig) {
    this.id = CodeMirror.guid.getNext();
    this.config = config;

    const webflowKeymap: KeyBinding[] = [
      {
        key: 'Enter',
        run: (view) => config.onEnter?.(view) ?? false,
      },
      {
        key: 'Mod-Shift-p',
        run: () => {
          if (config.onFormat) {
            config.onFormat();
            return true;
          }
          return false;
        },
        preventDefault: true,
      },
      {
        key: 'Mod-s',
        run: () => {
          if (config.onSave) {
            config.onSave();
            return true;
          }
          return false;
        },
        preventDefault: true,
      },
    ];

    // Tab/Shift-Tab use Prec.low so that Emmet's Tab handler (at default
    // priority inside the emmet compartment) fires first when an active
    // abbreviation is present.  When Emmet's handler returns false (no
    // abbreviation tracked), CM6 falls through to these low-priority
    // bindings and inserts a normal tab/indent.
    const tabKeymap = Prec.low(
      keymap.of([
        {
          key: 'Tab',
          run: insertTab,
          preventDefault: true,
        },
        {
          key: 'Shift-Tab',
          run: (view) => {
            if (config.onFormat) {
              config.onFormat();
              return true;
            }
            return indentLess(view);
          },
          preventDefault: true,
        },
      ])
    );

    const singleLineConfig: Extension[] = [];
    if (config.singleLine) {
      singleLineConfig.push(
        singleLineMode(),
        EditorView.contentAttributes.of({'aria-multiline': 'false'})
      );
    }

    const lineNumbersConfig: Extension[] = [];
    if (config.lineNumbers) {
      lineNumbersConfig.push(
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightActiveLine()
      );
    }

    const lineWrappingConfig: Extension[] = [];
    if (config.lineWrapping) {
      lineWrappingConfig.push(EditorView.lineWrapping);
    }

    const placeholderConfig: Extension[] = [];
    if (config.placeholder) {
      placeholderConfig.push(placeholder(config.placeholder));
    }

    const readOnlyConfig: Extension[] = [];
    if (config.readOnly) {
      readOnlyConfig.push(
        EditorView.editable.of(false),
        EditorState.readOnly.of(true)
      );
    }

    const indentMarkersConfig: Extension[] = [];
    if (config.indentMarkers) {
      indentMarkersConfig.push(indentationMarkers());
    }

    const markdownConfig: Extension[] = [];
    if (config.markdown) {
      markdownConfig.push(markdownGfm({codeLanguages: languages}));
    }

    // Build search extensions for multi-line editors
    // Use Prec.highest to ensure search keymap runs before browser defaults
    const searchExtensions: Extension[] = [];
    if (!config.singleLine) {
      searchExtensions.push(
        Prec.highest(keymap.of(searchKeymap)), // Highest priority to intercept Cmd/Ctrl+F
        search({top: true, createPanel: createSearchPanel}),
        replaceVisibleExtension
      );
      if (config.highlightSelectionMatches !== false) {
        searchExtensions.push(highlightSelectionMatches());
      }
    }

    const extensions: Extension[] = [
      EditorView.editorAttributes.of({id: this.id}),
      ...searchExtensions, // Add search keymap BEFORE other keymaps
      keymap.of(webflowKeymap),
      tabKeymap,
      keymap.of(defaultKeymap),
      keymap.of(historyKeymap),
      keymap.of(closeBracketsKeymap),
      closeBrackets(),
      // When the editor supports {{ }} token insertion (hasTokens), exclude {
      // from auto-closing to avoid mangling token syntax (typing { inserts {},
      // so {{ becomes {{}). Otherwise, include { for normal brace auto-closing.
      EditorState.languageData.of(() => [
        {
          closeBrackets: {
            brackets: config.hasTokens
              ? ['(', '[', "'", '"']
              : ['(', '[', '{', "'", '"'],
          },
        },
      ]),
      autocompletion(),
      // Tell CM6 the available tooltip space so autocomplete flips above/below correctly.
      // Without this, tooltips clip against the modal boundary because CM6 defaults to the
      // full window viewport. We use the editor's scrollable parent as the bounding rect.
      tooltips({
        tooltipSpace: (view) => {
          const parent = view.dom.closest('.cm-editor')?.parentElement;
          if (parent) {
            const rect = parent.getBoundingClientRect();
            return {
              top: rect.top,
              left: rect.left,
              bottom: rect.bottom,
              right: rect.right,
            };
          }
          return {top: 0, left: 0, bottom: innerHeight, right: innerWidth};
        },
      }),
      highlightSpecialChars(),
      drawSelection(),
      EditorState.allowMultipleSelections.of(true),
      rectangularSelection(),
      crosshairCursor(),
      this.onUpdate,
      // Fix horizontal trackpad/mouse-wheel scrolling. CM6 intercepts wheel events
      // for its virtual viewport, which prevents native horizontal scroll on .cm-scroller.
      // This plugin attaches a wheel listener directly to scrollDOM and manually applies
      // the horizontal delta so trackpad two-finger horizontal swipes work.
      ...(!config.singleLine
        ? [
            ViewPlugin.fromClass(
              class {
                private handler: (e: WheelEvent) => void;
                constructor(view: EditorView) {
                  this.handler = (e: WheelEvent) => {
                    if (e.deltaX !== 0) {
                      view.scrollDOM.scrollLeft += e.deltaX;
                    }
                  };
                  view.scrollDOM.addEventListener('wheel', this.handler, {
                    passive: true,
                  });
                }
                destroy() {
                  // view.scrollDOM is destroyed with the editor — no cleanup needed
                }
              }
            ),
          ]
        : []),
      this.compartments.highlightStyle.of(
        syntaxHighlighting(
          getHighlightStyleForTheme(config.theme, config.highlightStyle)
        )
      ),
      this.compartments.domEventHandlers.of(this.createDomEventHandlers()),
      this.compartments.history.of(history()),
      this.compartments.language.of([]),
      this.compartments.singleLine.of(singleLineConfig),
      this.compartments.lineNumbers.of(lineNumbersConfig),
      this.compartments.lineWrapping.of(lineWrappingConfig),
      this.compartments.placeholder.of(placeholderConfig),
      this.compartments.readOnly.of(readOnlyConfig),
      this.compartments.theme.of(createTheme(config)),
      this.compartments.indentMarkers.of(indentMarkersConfig),
      this.compartments.markdown.of(markdownConfig),
      this.compartments.emmet.of([]),
      ...(config.extensions || []),
    ];

    this.editor = new EditorView({
      doc: config.initialDoc,
      parent: config.element,
      extensions,
    });

    if (config.language) {
      this.loadLanguage(config.language);
    }
  }

  static viewFromElement(element?: HTMLElement) {
    const content = element?.querySelector?.('.cm-content') as any;
    const tile = content?.cmTile || (element as any)?.cmTile;
    return tile?.root?.view as EditorView | undefined;
  }

  get view(): EditorView {
    return this.editor;
  }

  get dom() {
    return this.editor.dom;
  }

  get scroller() {
    return this.editor.scrollDOM;
  }

  get state() {
    return this.editor.state;
  }

  get selection() {
    return this.editor.state.selection;
  }

  get placeholder() {
    return this.config.placeholder;
  }

  set placeholder(value: string | undefined) {
    this.config.placeholder = value;
    this.editor.dispatch({
      effects: this.compartments.placeholder.reconfigure(
        value ? placeholder(value) : []
      ),
    });
  }

  get isReadOnly() {
    return this.editor.state.readOnly;
  }

  set isReadOnly(value: boolean) {
    this.editor.dispatch({
      effects: this.compartments.readOnly.reconfigure([
        EditorView.editable.of(!value),
        EditorState.readOnly.of(value),
      ]),
    });
  }

  get lineWrapping() {
    return this.config.lineWrapping ?? false;
  }

  set lineWrapping(value: boolean) {
    this.config.lineWrapping = value;
    this.editor.dispatch({
      effects: this.compartments.lineWrapping.reconfigure(
        value ? EditorView.lineWrapping : []
      ),
    });
  }

  get theme() {
    const {theme, height, fontFamily, singleLine} = this.config;
    return {
      theme,
      height,
      fontFamily,
      singleLine,
    };
  }

  set theme(value: ThemeConfig) {
    this.config.theme = value.theme;
    this.editor.dispatch({
      effects: [
        this.compartments.theme.reconfigure(createTheme(value)),
        this.compartments.highlightStyle.reconfigure(
          syntaxHighlighting(
            getHighlightStyleForTheme(value.theme, this.config.highlightStyle)
          )
        ),
      ],
    });
  }

  get highlightStyle() {
    return this.config.highlightStyle;
  }

  set highlightStyle(value: EditorConfig['highlightStyle']) {
    this.config.highlightStyle = value;
    this.editor.dispatch({
      effects: this.compartments.highlightStyle.reconfigure(
        syntaxHighlighting(
          getHighlightStyleForTheme(
            this.config.theme,
            this.config.highlightStyle
          )
        )
      ),
    });
  }

  get language() {
    return this.config.language;
  }

  set language(value: LanguageName | string | undefined) {
    this.config.language = value;
    if (value) {
      this.loadLanguage(value);
    } else {
      this.editor.dispatch({
        effects: [
          this.compartments.language.reconfigure([]),
          this.compartments.emmet.reconfigure([]),
        ],
      });
    }
  }

  async setLanguage(desc: LanguageDescription) {
    const support = await desc.load();
    this.editor.dispatch({
      effects: this.compartments.language.reconfigure(support),
    });
  }

  get markdown() {
    return this.config.markdown;
  }

  set markdown(value: boolean | undefined) {
    this.config.markdown = value;
    this.editor.dispatch({
      effects: this.compartments.markdown.reconfigure(
        value ? markdownGfm({codeLanguages: languages}) : []
      ),
    });
  }

  setEventHandler<T extends 'focus' | 'blur' | 'scroll' | 'keydown'>(
    event: T,
    handler: DOMEventHandlers<any>[T]
  ) {
    const nameMap: Record<keyof DOMEventHandlers<any>, keyof EditorConfig> = {
      focus: 'onFocus',
      blur: 'onBlur',
      scroll: 'onScroll',
      keydown: 'onKeyDown',
    } as const;
    this.config[nameMap[event]] = handler as any; // Handler is guaranteed to match the type for the event
    this.editor.dispatch({
      effects: this.compartments.domEventHandlers.reconfigure(
        this.createDomEventHandlers()
      ),
    });
  }

  resetHistory() {
    this.editor.dispatch({
      effects: this.compartments.history.reconfigure([]),
    });
    this.editor.dispatch({
      effects: this.compartments.history.reconfigure(history()),
    });
  }

  dispatch(transaction: TransactionSpec) {
    this.editor.dispatch(transaction);
  }

  focus(delay?: number) {
    if (delay !== undefined) {
      setTimeout(() => this.editor.focus(), delay);
    } else {
      this.editor.focus();
    }
  }

  plugin<T extends PluginValue>(plugin?: ViewPlugin<T>) {
    return plugin ? this.editor.plugin(plugin) : null;
  }

  dispose() {
    // Clean up typing timeout
    if (this.typingTimeoutId !== null) {
      clearTimeout(this.typingTimeoutId);
      this.typingTimeoutId = null;
    }
    // Flush any pending debounced onChange to ensure final state is saved
    this.onChangeDebounced.flush();
    this.editor.destroy();
  }

  /**
   * Drops the debounced `onChange` still owed for the current document and ends
   * the typing session, so the next external value update is applied instead of
   * being skipped as a mid-typing overwrite.
   *
   * For a parent that is swapping the document out from under the user and has
   * already taken the pending text somewhere else -- otherwise those keystrokes
   * arrive up to 200ms later and get attributed to whatever is open by then.
   * `dispose` flushes rather than cancels because there is no next document.
   */
  cancelPendingChange() {
    this.onChangeDebounced.cancel();
    if (this.typingTimeoutId !== null) {
      clearTimeout(this.typingTimeoutId);
      this.typingTimeoutId = null;
    }
    this.isTyping = false;
  }

  private onChangeDebounced = debounce(() => {
    // Get the current state directly from the editor when the debounced function executes
    // This ensures we always get the latest content, not a stale update object
    const currentContent = this.editor.state.doc.toString();
    const timestamp = performance.now();
    this.config.onChange?.(currentContent, timestamp);
  }, CodeMirror.changeDebounceMs);

  private onUpdate = EditorView.updateListener.of((update) => {
    if (update.docChanged) {
      // Check if this is a user-initiated change (not from external updates)
      const isUserChange = !update.transactions.some((tr) =>
        tr.annotation(skipOnChange)
      );

      if (isUserChange) {
        // User is actively typing - set typing state
        this.isTyping = true;

        // Clear any existing typing timeout
        if (this.typingTimeoutId !== null) {
          clearTimeout(this.typingTimeoutId);
        }

        // Set a timeout to clear typing state after user stops typing
        this.typingTimeoutId = window.setTimeout(() => {
          this.isTyping = false;
          this.typingTimeoutId = null;
        }, CodeMirror.changeDebounceMs); // 200ms after last keystroke, consider typing session ended
      }

      if (this.config.onChange && isUserChange) {
        this.onChangeDebounced();
        // Discrete actions (widget removal/modification) annotate their transactions with
        // flushOnChange to bypass the 200ms debounce. Without this, closing the editor
        // immediately after a widget action can lose the change — dispose().flush() fires
        // during unmount when the parent callback chain is tearing down.
        const shouldFlush = update.transactions.some((tr) =>
          tr.annotation(flushOnChange)
        );
        if (shouldFlush) {
          this.onChangeDebounced.flush();
        }
      }
    }
  });

  private async loadLanguage(name: string) {
    const desc = LanguageDescription.matchLanguageName(languages, name, false);

    // Clear Emmet extensions immediately so the old syntax config doesn't
    // produce wrong-language expansions while the new extensions load.
    if (this.config.emmet) {
      this.editor.dispatch({
        effects: this.compartments.emmet.reconfigure([]),
      });
    }

    if (desc) {
      await this.setLanguage(desc);
    }
    // Load Emmet extensions for supported languages (HTML, CSS, JSX, TSX).
    if (this.config.emmet) {
      const emmetExtensions = await createEmmetExtensions(name);
      this.editor.dispatch({
        effects: this.compartments.emmet.reconfigure(emmetExtensions),
      });
    }
  }

  private createDomEventHandlers() {
    const config = this.config;
    const domHandlers = EditorView.domEventHandlers({
      keydown: (event, view) => {
        // Prevent browser's default search shortcuts when in multi-line editor
        if (!config.singleLine && (event.metaKey || event.ctrlKey)) {
          // Cmd/Ctrl+F - Open search
          if (event.key === 'f' && !event.shiftKey && !event.altKey) {
            event.preventDefault();
            openSearchPanel(view);
            return true;
          }
          // Cmd/Ctrl+G - Find next
          // Shift+Cmd/Ctrl+G - Find previous
          if (event.key === 'g' && !event.altKey) {
            event.preventDefault();
            if (event.shiftKey) {
              findPrevious(view);
            } else {
              findNext(view);
            }
            return true;
          }
        }
        config.onKeyDown?.(event, view);
      },
      focus: config.onFocus,
      blur: config.onBlur,
      scroll: config.onScroll,
    });
    return domHandlers;
  }
}
